import { useMemo, useState, type ReactElement } from 'react';
import { useTranslation } from '@govard/i18n-core/react';
import { Card, Tabs, Modal, Button, Spin, Typography, Descriptions } from 'antd';

import { useUpdateWorkspaceSettingsMutation, useWorkspaceSettings } from '../features/settings/hooks/useSettings.js';
import { useCurrentScope, useCurrentUser, useCurrentWorkspaceSettings } from '../shared/scope/useCurrentScope.js';

const { Title, Text } = Typography;

export default function SettingsPage(): ReactElement {
  const { t } = useTranslation();
  const user = useCurrentUser();
  const scope = useCurrentScope();
  const workspaceSettings = useCurrentWorkspaceSettings();
  const [activeTab, setActiveTab] = useState<string>('profile');
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const settingsQuery = useWorkspaceSettings();
  const updateSettingsMutation = useUpdateWorkspaceSettingsMutation();

  const proModeEnabled = settingsQuery.data?.featureFlags['pro-mode'] ?? false;
  const tabs = useMemo(
    () => [
      { key: 'profile', label: t('crm.settings.tabs.profile') },
      { key: 'workspace', label: t('crm.settings.tabs.workspace') },
      { key: 'ai', label: t('crm.settings.tabs.ai') },
      { key: 'flags', label: <span data-testid="settings-tab-flags">{t('crm.settings.tabs.flags')}</span> },
    ],
    [t],
  );

  async function handleConfirmProMode(): Promise<void> {
    await updateSettingsMutation.mutateAsync({ featureFlags: { 'pro-mode': true } });
    setIsConfirmOpen(false);
  }

  async function handleToggleProMode(): Promise<void> {
    if (proModeEnabled) {
      await updateSettingsMutation.mutateAsync({ featureFlags: { 'pro-mode': false } });
      return;
    }
    setIsConfirmOpen(true);
  }

  const renderTabContent = () => {
    if (activeTab === 'profile') {
      return (
        <Card bordered={false} style={{ boxShadow: '0 1px 2px rgba(0,0,0,0.05)' }}>
          <Descriptions title={t('crm.settings.tabs.profile')} column={1}>
            <Descriptions.Item label="Email">{user?.email ?? '—'}</Descriptions.Item>
            <Descriptions.Item label="Имя">{user?.name ?? '—'}</Descriptions.Item>
            <Descriptions.Item label="Роль">{scope?.role ?? '—'}</Descriptions.Item>
          </Descriptions>
        </Card>
      );
    }
    if (activeTab === 'workspace') {
      return (
        <Card bordered={false} style={{ boxShadow: '0 1px 2px rgba(0,0,0,0.05)' }}>
          <Descriptions title={t('crm.settings.tabs.workspace')} column={1}>
            <Descriptions.Item label="Workspace ID">{workspaceSettings?.workspaceId ?? scope?.workspaceId ?? '—'}</Descriptions.Item>
            <Descriptions.Item label="Feature flags">{Object.keys(settingsQuery.data?.featureFlags ?? {}).join(', ') || '—'}</Descriptions.Item>
          </Descriptions>
        </Card>
      );
    }
    if (activeTab === 'ai') {
      return (
        <Card bordered={false} style={{ boxShadow: '0 1px 2px rgba(0,0,0,0.05)' }}>
          <Descriptions title={t('crm.settings.tabs.ai')} column={1}>
            <Descriptions.Item label="AI Chat">Включен в карточке сделки</Descriptions.Item>
            <Descriptions.Item label="Биллинг">{scope?.isBillable ? 'Включен' : 'Выключен'}</Descriptions.Item>
          </Descriptions>
        </Card>
      );
    }
    if (activeTab === 'flags') {
      return (
        <Card aria-label={t('crm.settings.tabs.flags')} bordered={false} style={{ boxShadow: '0 1px 2px rgba(0,0,0,0.05)' }}>
          <div className="mb-6">
            <Title level={5} style={{ marginBottom: 4 }}>{t('crm.settings.flags_title')}</Title>
            <Text type="secondary">{t('crm.settings.flags_description')}</Text>
          </div>

          <Card type="inner" style={{ backgroundColor: '#fffdfa', borderColor: '#e7e5e4' }}>
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <Text strong style={{ display: 'block' }}>{t('crm.settings.pro_mode_title')}</Text>
                <Text type="secondary">{t('crm.settings.pro_mode_description')}</Text>
              </div>
              <Button
                data-testid="flag-pro-mode-toggle"
                type={proModeEnabled ? 'default' : 'primary'}
                loading={updateSettingsMutation.isPending}
                disabled={settingsQuery.isLoading}
                onClick={() => void handleToggleProMode()}
              >
                {proModeEnabled ? t('crm.settings.disable_pro_mode') : t('crm.settings.enable_pro_mode')}
              </Button>
            </div>
          </Card>
        </Card>
      );
    }
    return null;
  };

  return (
    <main
      className="flex flex-col p-6 mx-auto w-full h-full min-h-screen"
      style={{
        backgroundColor: '#f9f7f4',
        maxWidth: 1200,
        gap: 24,
      }}
    >
      <div className="mb-2">
        <Title level={2} style={{ margin: 0, fontWeight: 700, color: '#44403c' }}>
          {t('crm.settings.title')}
        </Title>
        <Text type="secondary" style={{ fontSize: 14 }}>
          {t('crm.settings.subtitle')}
        </Text>
      </div>

      <Card
        bordered={false}
        styles={{ body: { padding: '24px 24px 40px' } }}
        style={{ boxShadow: '0 1px 2px rgba(0,0,0,0.05)', flex: 1 }}
      >
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          items={tabs}
          className="ant-tabs-warm"
        />

        <div className="mt-6 w-full max-w-3xl">
          {settingsQuery.isLoading ? (
            <div className="flex items-center gap-2 py-8">
              <Spin size="small" /> <Text type="secondary">{t('crm.settings.loading')}</Text>
            </div>
          ) : (
            renderTabContent()
          )}
        </div>
      </Card>

      <Modal
        title={t('crm.settings.confirm_title')}
        open={isConfirmOpen}
        onCancel={() => setIsConfirmOpen(false)}
        footer={[
          <Button key="back" onClick={() => setIsConfirmOpen(false)}>
            {t('crm.common.cancel')}
          </Button>,
          <Button
            key="submit"
            type="primary"
            loading={updateSettingsMutation.isPending}
            onClick={() => void handleConfirmProMode()}
          >
            {t('crm.settings.confirm_submit')}
          </Button>,
        ]}
      >
        <p data-testid="pro-enable-confirm">{t('crm.settings.confirm_body')}</p>
      </Modal>
    </main>
  );
}
